import { Dispatch } from "redux";
import {
  DistributionList,
  Meta,
  CaseFollow,
  CaseFollowAttributes,
  DistributionListAttributes,
} from "typings";
import { DistributionLists, CaseFollows } from "@pacerpro/api";
import { editTrackInTable } from "./tracks";

type DistributionListsPayload = {
  data: DistributionList[];
  included: any[];
  meta?: Meta;
  links: {
    first: string;
    prev?: string;
    next?: string;
    last: string;
  };
  filteredTotal: number;
};

export type DistributionListPayload = {
  data: DistributionList;
  included: any[];
  meta?: Meta;
  links: {
    first: string;
    prev?: string;
    next?: string;
    last: string;
  };
};

const initialDistributionList: DistributionList = {
  type: "distributionLists",
  id: "",
  attributes: {
    clientCode: "",
    updateFrequency: "",
    owner: "",
    createdAt: "",
    activityAt: "",
    docketEntriesCount: 0,
    receivedNefFromCourt: false,
  },
  relationships: {
    case: {
      links: {
        self: "",
      },
    },
    users: {
      links: {
        self: "",
      },
    },
    members: {
      links: {
        self: "",
      },
    },
    organization: {
      links: {
        self: "",
      },
    },
  },
  links: {
    self: "",
  },
};

/////////////////////////////////////////////////
// DISTRIBUTION LIST CONSTANTS
/////////////////////////////////////////////////

export const SET_DISTRIBUTION_LISTS = "SET_DISTRIBUTION_LISTS";
export const SET_DISTRIBUTION_LISTS_PAGE = "SET_DISTRIBUTION_LISTS_PAGE";
export const SET_DISTRIBUTION_LISTS_LOADING = "SET_DISTRIBUTION_LISTS_LOADING";
export const SET_DISTRIBUTION_LISTS_NAV_LOADING =
  "SET_DISTRIBUTION_LISTS_NAV_LOADING";
export const SET_MORE_DISTRIBUTION_LISTS = "SET_MORE_DISTRIBUTION_LISTS";
export const ADD_DISTRIBUTION_LISTS = "ADD_DISTRIBUTION_LISTS";
export const UNSET_DISTRIBUTION_LISTS = "UNSET_DISTRIBUTION_LISTS";
export const SET_SELECTED_DISTRIBUTION_LIST = "SET_SELECTED_DISTRIBUTION_LIST";
export const SET_SELECTED_DISTRIBUTION_LIST_LOADING =
  "SET_SELECTED_DISTRIBUTION_LIST_LOADING";
export const UNSET_SELECTED_DISTRIBUTION_LIST =
  "UNSET_SELECTED_DISTRIBUTION_LIST";
export const EDIT_SELECTED_DISTRIBUTION_LIST =
  "EDIT_SELECTED_DISTRIBUTION_LIST";
export const EDIT_DISTRIBUTION_LIST_IN_TABLE =
  "EDIT_DISTRIBUTION_LIST_IN_TABLE";
export const UPDATE_INCLUDED_CASE_FOLLOW = "UPDATE_INCLUDED_CASE_FOLLOW";
export const SET_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LIST =
  "SET_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LIST";
export const CLEAR_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LISTS =
  "CLEAR_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LISTS";

/////////////////////////////////////////////////
// DISTRIBUTION LIST ACTIONS
/////////////////////////////////////////////////

export const setDistributionLists = ({
  data = [],
  included = [],
  meta,
  links,
  filteredTotal = 0,
}: DistributionListsPayload) => ({
  type: SET_DISTRIBUTION_LISTS,
  payload: { data, included, meta, links, filteredTotal },
});

export const setMoreDistributionLists = ({
  data = [],
  included = [],
  links,
}: DistributionListsPayload) => ({
  type: SET_MORE_DISTRIBUTION_LISTS,
  payload: { data, included, links },
});

export const setDistributionListsLoading = (payload: boolean) => ({
  type: SET_DISTRIBUTION_LISTS_LOADING,
  payload,
});

export const setDistributionListsNavLoading = (payload: boolean) => ({
  type: SET_DISTRIBUTION_LISTS_NAV_LOADING,
  payload,
});

export const unsetDistributionLists = () => ({
  type: UNSET_DISTRIBUTION_LISTS,
});

export const setSelectedDistributionList = ({
  data = initialDistributionList,
  included = [],
}: DistributionListPayload) => ({
  type: SET_SELECTED_DISTRIBUTION_LIST,
  payload: { data, included },
});

export const setSelectedDistributionListLoading = (payload: boolean) => ({
  type: SET_SELECTED_DISTRIBUTION_LIST_LOADING,
  payload,
});

export const updateIncludedCaseFollow = (payload: {
  data: CaseFollow;
  included?: any[];
}) => ({
  type: UPDATE_INCLUDED_CASE_FOLLOW,
  payload,
});

export const unsetSelectedDistributionList = () => ({
  type: UNSET_SELECTED_DISTRIBUTION_LIST,
});

export const editDistributionListInTable = ({
  data,
  included = [],
}: DistributionListPayload) => ({
  type: EDIT_DISTRIBUTION_LIST_IN_TABLE,
  payload: { data, included },
});

export const loadMoreDistributionLists = (options: {
  sort?: string;
  filter?: { [key: string]: string | undefined };
  number?: number;
  size?: number;
  include?: string[];
}) => async (dispatch: Dispatch) => {
  try {
    dispatch(setDistributionListsLoading(true));
    const distributionListsResponse = await DistributionLists.getAll(options);
    dispatch(setDistributionListsLoading(false));
    dispatch({
      type: SET_DISTRIBUTION_LISTS_PAGE,
      payload: options.number,
    });
    dispatch(setMoreDistributionLists(distributionListsResponse.data));
  } catch (error) {
    console.error(error);
  }
};

export const loadSelectedDistributionList = (
  distributionListId: string,
) => async (dispatch: Dispatch) => {
  try {
    const distributionListResponse = await DistributionLists.getById(
      distributionListId,
      {
        include: ["case", "members", "members.user"],
      },
    );
    dispatch(setSelectedDistributionList(distributionListResponse.data));
    dispatch(editDistributionListInTable(distributionListResponse.data));
    dispatch(editTrackInTable(distributionListResponse.data));
    dispatch(setSelectedDistributionListLoading(false));
  } catch (error) {
    console.error(error);
  }
};

export const editSelectedDistributionList = (
  distributionListId: string,
  distributionListAttributes: DistributionListAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const distributionListResponse = await DistributionLists.edit(
      distributionListId,
      distributionListAttributes,
      {
        include: [
          "case",
          "users",
          "users.caseFollows",
          "users.caseFollows.distributionList",
        ],
      },
    );
    dispatch({
      type: EDIT_SELECTED_DISTRIBUTION_LIST,
      payload: distributionListResponse.data,
    });
  } catch (error) {
    console.error(error);
  }
};

export const editDistributionList = (
  distributionListId: string,
  distributionListAttributes: DistributionListAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const distributionListResponse = await DistributionLists.edit(
      distributionListId,
      distributionListAttributes,
      {
        include: ["case", "members"],
      },
    );
    dispatch({
      type: EDIT_DISTRIBUTION_LIST_IN_TABLE,
      payload: distributionListResponse.data,
    });
  } catch (error) {
    console.error(error);
  }
};

export const editDistributionListCaseFollow = (
  caseFollowId: string,
  caseFollowAttributes: CaseFollowAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const caseFollowResponse = await CaseFollows.edit(
      caseFollowId,
      caseFollowAttributes,
      {
        include: ["user"],
      },
    );
    return dispatch(updateIncludedCaseFollow(caseFollowResponse.data));
  } catch (error) {
    console.error(error);
  }
};

export const updateRecentlyAddedFollowToDistributionList = (
  recentlyAddedFollowList: Array<string>,
  caseTitle?: string,
) => {
  return {
    type: SET_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LIST,
    payload: {
      recentlyAddedFollowList,
      caseTitle: caseTitle || null,
    },
  };
};

export const clearRecentlyAddedFollowToDistributionList = () => ({
  type: CLEAR_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LISTS,
});

export type DistributionListAction =
  | {
      type: typeof SET_DISTRIBUTION_LISTS;
      payload: DistributionListsPayload;
    }
  | {
      type: typeof SET_DISTRIBUTION_LISTS_PAGE;
      payload: number;
    }
  | {
      type: typeof SET_MORE_DISTRIBUTION_LISTS;
      payload: DistributionListsPayload;
    }
  | {
      type: typeof SET_SELECTED_DISTRIBUTION_LIST;
      payload: {
        data: DistributionList;
        included: any[];
      };
    }
  | {
      type: typeof SET_SELECTED_DISTRIBUTION_LIST_LOADING;
      payload: boolean;
    }
  | {
      type: typeof SET_DISTRIBUTION_LISTS_LOADING;
      payload: boolean;
    }
  | {
      type: typeof SET_DISTRIBUTION_LISTS_NAV_LOADING;
      payload: boolean;
    }
  | {
      type: typeof UPDATE_INCLUDED_CASE_FOLLOW;
      payload: {
        data: CaseFollow;
        included: any[];
      };
    }
  | {
      type: typeof EDIT_SELECTED_DISTRIBUTION_LIST;
      payload: {
        data: DistributionList;
        included: any[];
      };
    }
  | {
      type: typeof EDIT_DISTRIBUTION_LIST_IN_TABLE;
      payload: {
        data: DistributionList;
        included: any[];
      };
    }
  | {
      type: typeof UNSET_SELECTED_DISTRIBUTION_LIST;
    }
  | {
      type: typeof UNSET_DISTRIBUTION_LISTS;
    }
  | {
      type: typeof SET_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LIST;
      payload: {
        recentlyAddedFollowList: Array<string>;
        caseTitle?: string;
      };
    }
  | {
      type: typeof CLEAR_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LISTS;
    };

/////////////////////////////////////////////////
// DISTRIBUTION LIST REDUCER
/////////////////////////////////////////////////

export interface DistributionListState {
  page: number;
  isLoading: boolean;
  navLoading: boolean;
  selectedLoading: boolean;
  filteredTotal: number;
  distributionLists: {
    data: DistributionList[];
    included: any[];
    meta: Meta;
    links?: {
      first: string;
      prev?: string;
      next?: string;
      last: string;
    };
  };
  distributionList: {
    data: DistributionList;
    included: any[];
    meta: Meta;
    links?: any;
    recentlyAdded?: any;
    recentlyAddedCaseTitle?: string;
  };
}

const initialState: DistributionListState = {
  page: 1,
  isLoading: false,
  navLoading: false,
  selectedLoading: false,
  filteredTotal: 0,
  distributionLists: {
    data: [],
    included: [],
    meta: {
      recordCount: 0,
      pageCount: 0,
    },
    links: {
      first: "",
      prev: "",
      next: "",
      last: "",
    },
  },
  distributionList: {
    data: initialDistributionList,
    included: [],
    meta: {
      recordCount: 0,
      pageCount: 0,
    },
    links: {},
    recentlyAdded: [],
    recentlyAddedCaseTitle: "",
  },
};

export default (state = initialState, action: DistributionListAction) => {
  switch (action.type) {
    case SET_SELECTED_DISTRIBUTION_LIST: {
      return {
        ...state,
        distributionList: {
          ...state.distributionList,
          data: action.payload.data,
          included: action.payload.included,
        },
      };
    }
    case SET_SELECTED_DISTRIBUTION_LIST_LOADING: {
      return {
        ...state,
        selectedLoading: action.payload,
      };
    }
    case UNSET_SELECTED_DISTRIBUTION_LIST: {
      return {
        ...state,
        distributionList: {
          data: initialState.distributionList.data,
          included: initialState.distributionList.included,
          meta: initialState.distributionList.meta,
          recentlyAdded: [],
          recentlyAddedCaseTitle: "",
        },
      };
    }
    case SET_DISTRIBUTION_LISTS: {
      return {
        ...state,
        isLoading: false,
        page: 1,
        filteredTotal: action.payload.filteredTotal,
        distributionLists: {
          ...state.distributionLists,
          data: action.payload.data,
          included: action.payload.included,
          meta: action.payload.meta || state.distributionLists.meta,
          links: action.payload.links,
        },
      };
    }
    case SET_MORE_DISTRIBUTION_LISTS: {
      return {
        ...state,
        distributionLists: {
          ...state.distributionLists,
          data: [...state.distributionLists.data, ...action.payload.data],
          included: [
            ...state.distributionLists.included,
            ...action.payload.included,
          ],
          meta: action.payload.meta || state.distributionLists.meta,
          links: action.payload.links,
        },
      };
    }
    case SET_DISTRIBUTION_LISTS_LOADING: {
      return {
        ...state,
        isLoading: action.payload,
      };
    }
    case SET_DISTRIBUTION_LISTS_NAV_LOADING: {
      return {
        ...state,
        navLoading: action.payload,
      };
    }
    case SET_DISTRIBUTION_LISTS_PAGE: {
      return {
        ...state,
        page: action.payload,
      };
    }
    case UNSET_DISTRIBUTION_LISTS: {
      return {
        ...state,
        distributionLists: {
          data: initialState.distributionLists.data,
          included: initialState.distributionLists.included,
          meta: initialState.distributionLists.meta,
        },
      };
    }
    case UPDATE_INCLUDED_CASE_FOLLOW: {
      return {
        ...state,
        distributionList: {
          ...state.distributionList,
          included: [...(state.distributionList.included || [])].map(included =>
            included.id === action.payload.data.id
              ? action.payload.data
              : included,
          ),
        },
      };
    }
    case EDIT_SELECTED_DISTRIBUTION_LIST: {
      return {
        ...state,
        distributionList: {
          ...state.distributionList,
          data: action.payload.data,
        },
      };
    }
    case EDIT_DISTRIBUTION_LIST_IN_TABLE: {
      return {
        ...state,
        distributionLists: {
          ...state.distributionLists,
          data: state.distributionLists.data.map(distributionList =>
            distributionList.id === action.payload.data.id
              ? action.payload.data
              : distributionList,
          ),
        },
      };
    }
    case SET_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LIST: {
      return {
        ...state,
        distributionList: {
          ...state.distributionList,
          recentlyAdded: action.payload.recentlyAddedFollowList,
          recentlyAddedCaseTitle: action.payload.caseTitle,
        },
      };
    }
    case CLEAR_RECENTLY_ADDED_FOLLOW_TO_DISTRIBUTION_LISTS: {
      return {
        ...state,
        distributionList: {
          ...state.distributionList,
          recentlyAdded: [],
          recentlyAddedCaseTitle: "",
        },
      };
    }
    default:
      return state;
  }
};
